/**
 * App Logger
 *
 * General-purpose logger for application-wide logging.
 * Wraps analyticsService with app-specific context.
 *
 * Optional fields are passed as a single `options` object so callers can't
 * accidentally swap `context` (extra data) with `error` (Error instance).
 * Firestore rejects custom class instances, so passing an Error in the wrong
 * slot used to break analytics batch writes.
 *
 * Usage:
 * ```typescript
 * import { appLogger } from '@/domain/services/analytics/appLogger';
 *
 * appLogger.debug('Debug message', 'ClassName.methodName', { context: { key: 'value' } });
 * appLogger.info('Info message', 'ClassName.methodName');
 * appLogger.warning('Warning message', 'ClassName.methodName', { context: { foo } });
 * appLogger.error('Error message', 'ClassName.methodName', { context: { foo }, error });
 * ```
 */

import { analyticsService, type LogOptions } from './analyticsService';

export type { LogOptions };

class AppLogger {
  public debug(message: string, source: string, options?: LogOptions): void {
    analyticsService.debug(message, `App.${source}`, options);
  }

  public info(message: string, source: string, options?: LogOptions): void {
    analyticsService.info(message, `App.${source}`, options);
  }

  public warning(message: string, source: string, options?: LogOptions): void {
    analyticsService.warning(message, `App.${source}`, options);
  }

  public error(message: string, source: string, options?: LogOptions): void {
    analyticsService.error(message, `App.${source}`, options);
  }

  public async flush(): Promise<void> {
    await analyticsService.flush();
  }
}

export const appLogger = new AppLogger();
